import * as THREE from "three";
import { setTerrainHeightProvider } from "./terrain";
import { paintFarFieldVertexColors } from "./terrain-materials";
import { addHorizonRing } from "./horizon";
import { IRON_CURTAIN_ROUTE } from "./nav";

// IRON CURTAIN (map05) — Theater 4 in THEME.md: inner German border, autumn
// 1989, overcast. Flat farmland quantized into a crisp field patchwork (the
// signature read from orbit), treelines on the field boundaries, a carved
// zigzag trench network mid-route, and the death strip: plowed bare earth,
// double fence, concrete watchtowers. Stylized-only.

const ROUTE = IRON_CURTAIN_ROUTE.map((n) => n.position);

// The border strip runs the same diagonal family as the route's far end —
// the crossing point sits ON it.
export function borderStripAt(x: number, z: number): number {
  return Math.abs(x + z - 760) / Math.SQRT2;
}

// Two zigzag trench lines crossing the route around THE TRENCHES node —
// carved into the height field and painted as dark scars.
const TRENCH_LINES: Array<Array<[number, number]>> = [
  [
    [-30, -190],
    [-95, -120],
    [-45, -75],
    [-120, -5],
    [-70, 55],
    [-140, 120],
  ],
  [
    [40, -140],
    [-20, -80],
    [30, -25],
    [-40, 45],
    [15, 105],
  ],
];

function smoothNoise(x: number, z: number, scale: number): number {
  return (
    Math.sin(x * scale) * 0.42 +
    Math.cos(z * scale * 1.17) * 0.36 +
    Math.sin((x + z) * scale * 0.63) * 0.22
  ) * 0.5 + 0.5;
}

function distanceToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const abx = bx - ax;
  const abz = bz - az;
  const lenSq = abx * abx + abz * abz;
  const t = lenSq <= 1e-6 ? 0 : THREE.MathUtils.clamp(((px - ax) * abx + (pz - az) * abz) / lenSq, 0, 1);
  return Math.hypot(px - (ax + abx * t), pz - (az + abz * t));
}

export function map05RouteDistanceAt(x: number, z: number): number {
  let best = Infinity;
  for (let i = 0; i < ROUTE.length - 1; i++) {
    const a = ROUTE[i]!;
    const b = ROUTE[i + 1]!;
    best = Math.min(best, distanceToSegment(x, z, a.x, a.z, b.x, b.z));
  }
  return best;
}

export function trenchDistanceAt(x: number, z: number): number {
  let best = Infinity;
  for (const line of TRENCH_LINES) {
    for (let i = 0; i < line.length - 1; i++) {
      const a = line[i]!;
      const b = line[i + 1]!;
      best = Math.min(best, distanceToSegment(x, z, a[0], a[1], b[0], b[1]));
    }
  }
  return best;
}

// --- Field patchwork ---------------------------------------------------------
// Farmland quantized into strips on a rotated grid. Field boundaries are
// shared by the paint (color changes), the treelines (planted on them), and
// nothing else — one source of truth for the patchwork.

const FIELD_ANGLE = 0.35; // radians — the grid leans against the route diagonal
const COS_A = Math.cos(FIELD_ANGLE);
const SIN_A = Math.sin(FIELD_ANGLE);
const STRIP_W = 120; // field strip width (u axis)
const BLOCK_L = 300; // field block length (v axis)

export function fieldCoords(x: number, z: number): { u: number; v: number } {
  return { u: x * COS_A + z * SIN_A, v: -x * SIN_A + z * COS_A };
}

function fieldHash(iu: number, iv: number): number {
  let h = Math.imul(iu, 0x9e3779b1) ^ Math.imul(iv, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 0xffffffff;
}

export function fieldIndexAt(x: number, z: number): { iu: number; iv: number; roll: number } {
  const { u, v } = fieldCoords(x, z);
  const iu = Math.floor(u / STRIP_W);
  const iv = Math.floor(v / BLOCK_L);
  return { iu, iv, roll: fieldHash(iu, iv) };
}

// Distance to the nearest field-strip boundary (for treeline planting)
function boundaryDistAt(x: number, z: number): number {
  const { u } = fieldCoords(x, z);
  const m = ((u % STRIP_W) + STRIP_W) % STRIP_W;
  return Math.min(m, STRIP_W - m);
}

// Near-flat farmland: long soft rises, trenches carved, the strip graded,
// edges fade under the far field.
export function map05HeightAt(x: number, z: number): number {
  let height = 3
    + (smoothNoise(x + 90, z - 30, 0.0028) - 0.5) * 14
    + (smoothNoise(x - 40, z + 70, 0.009) - 0.5) * 3.5;

  // Trenches are carved cuts
  const trench = 1 - THREE.MathUtils.smoothstep(trenchDistanceAt(x, z), 3, 9);
  height -= trench * 2.4;

  // The death strip is plowed and graded flat
  const strip = 1 - THREE.MathUtils.smoothstep(borderStripAt(x, z), 30, 64);
  height = THREE.MathUtils.lerp(height, 2.5, strip * 0.97);

  const edgeDist = Math.max(Math.abs(x), Math.abs(z));
  const edgeFade = 1 - THREE.MathUtils.smoothstep(edgeDist, 960, 1090);
  return THREE.MathUtils.lerp(-2, height, edgeFade);
}

// Autumn field palette — one color per field, hash-picked. Values sit close
// enough that the patchwork reads as one landscape, distinct enough that
// the boundaries are crisp from altitude.
const FIELD_COLORS = [
  new THREE.Color(0x9a8a56), // harvested gold
  new THREE.Color(0x6b5844), // plowed brown
  new THREE.Color(0x74844e), // late green
  new THREE.Color(0x8a7d52), // stubble
  new THREE.Color(0x7d6f4a), // dry hay
  new THREE.Color(0x62704a), // winter seed
];
const TREELINE_GROUND = new THREE.Color(0x4a4a34);
const TRENCH_EARTH = new THREE.Color(0x453b2e);
const ROAD_DIRT = new THREE.Color(0x7c7258);
const STRIP_EARTH = new THREE.Color(0x9b8d6c);

function paintMap05Ground(geo: THREE.PlaneGeometry): void {
  const pos = geo.attributes["position"] as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const color = new THREE.Color();

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const grain = smoothNoise(x, z, 0.03);

    const { roll } = fieldIndexAt(x, z);
    color.copy(FIELD_COLORS[Math.floor(roll * FIELD_COLORS.length) % FIELD_COLORS.length]!);
    color.offsetHSL(0, 0, (grain - 0.5) * 0.045);

    // Darker ground under the treeline boundaries
    const boundary = 1 - THREE.MathUtils.smoothstep(boundaryDistAt(x, z), 3, 8);
    if (boundary > 0) color.lerp(TREELINE_GROUND, boundary * 0.6);

    // Trench scars
    const trench = 1 - THREE.MathUtils.smoothstep(trenchDistanceAt(x, z), 4, 10);
    if (trench > 0) color.lerp(TRENCH_EARTH, trench * 0.85);

    // The route track
    const routeDist = map05RouteDistanceAt(x, z);
    const track = 1 - THREE.MathUtils.smoothstep(routeDist, 4, 10);
    if (track > 0) color.lerp(ROAD_DIRT, track * 0.85);

    // The plowed death strip
    const strip = 1 - THREE.MathUtils.smoothstep(borderStripAt(x, z), 28, 56);
    if (strip > 0) color.lerp(STRIP_EARTH, strip * 0.9);

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
}

const GROUND_MATERIAL = new THREE.MeshStandardMaterial({
  vertexColors: true,
  flatShading: true,
  roughness: 1.0,
  metalness: 0,
});

const TRACK_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0x746a52,
  roughness: 0.98,
  metalness: 0,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -2,
});

function resamplePolyline(points: THREE.Vector3[], step: number): THREE.Vector3[] {
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const segments = Math.max(1, Math.ceil(a.distanceTo(b) / step));
    for (let s = 0; s < segments; s++) out.push(a.clone().lerp(b, s / segments));
  }
  out.push(points[points.length - 1]!.clone());
  return out;
}

// Upward-wound ground ribbon (the hard-learned winding: base, base+2, base+1)
function makeTrackRibbon(points: THREE.Vector3[], width: number): THREE.Mesh {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const forward = new THREE.Vector3(next.x - prev.x, 0, next.z - prev.z).normalize();
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const p = points[i]!;
    const lx = p.x - right.x * width * 0.5;
    const lz = p.z - right.z * width * 0.5;
    const rx = p.x + right.x * width * 0.5;
    const rz = p.z + right.z * width * 0.5;
    positions.push(lx, map05HeightAt(lx, lz) + 0.16, lz);
    positions.push(rx, map05HeightAt(rx, rz) + 0.16, rz);
    if (i < points.length - 1) {
      const base = i * 2;
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const track = new THREE.Mesh(geo, TRACK_MATERIAL);
  track.name = "map05-track";
  track.receiveShadow = true;
  track.userData["losTransparent"] = true;
  return track;
}

// --- Dressing ----------------------------------------------------------------

function scatterRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) | 0;
    return (s >>> 0) / 0xffffffff;
  };
}

// Autumn treelines planted on field boundaries: russet/olive crown blobs in
// broken rows — the hedgerow read that says "European farmland" from orbit.
function addTreelines(parent: THREE.Group): void {
  const count = 2200;
  const material = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.95, metalness: 0 });
  const mesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1, 1), material, count);
  const rnd = scatterRng(0x1c554e1);
  const matrix = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const colorA = new THREE.Color(0x6d5a2c); // russet
  const colorB = new THREE.Color(0x556030); // fading olive
  const color = new THREE.Color();
  let placed = 0;
  let attempts = 0;
  while (placed < count && attempts < count * 40) {
    attempts++;
    const x = (rnd() - 0.5) * 2040;
    const z = (rnd() - 0.5) * 2040;
    if (Math.max(Math.abs(x), Math.abs(z)) > 920) continue;
    if (boundaryDistAt(x, z) > 6) continue; // on a field boundary
    if (rnd() < 0.3) continue; // broken rows, not solid walls
    if (map05RouteDistanceAt(x, z) < 18) continue;
    if (borderStripAt(x, z) < 70) continue; // the strip is kept clear
    if (trenchDistanceAt(x, z) < 12) continue;
    const r = 4 + rnd() * 4;
    quat.setFromAxisAngle(up, rnd() * Math.PI * 2);
    matrix.compose(
      new THREE.Vector3(x, map05HeightAt(x, z) + r * 0.9, z),
      quat,
      new THREE.Vector3(r, r * 0.85, r),
    );
    mesh.setMatrixAt(placed, matrix);
    color.copy(colorA).lerp(colorB, rnd());
    mesh.setColorAt(placed, color);
    placed++;
  }
  mesh.count = placed;
  mesh.name = "map05-treelines";
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.frustumCulled = false;
  mesh.userData["losTransparent"] = true;
  mesh.userData["ignoreBallistics"] = true;
  parent.add(mesh);
}

// Concrete watchtowers: leg column + cabin, the border's silhouette. Placed
// along the strip and two forward posts overlooking the route.
const TOWER_CONCRETE = new THREE.MeshStandardMaterial({ color: 0x8b8d87, flatShading: true, roughness: 0.9 });
const TOWER_CABIN = new THREE.MeshStandardMaterial({ color: 0x5f6660, flatShading: true, roughness: 0.85 });

export const WATCHTOWERS: Array<[number, number]> = [
  // Along the strip (x + z ≈ 760), flanking the crossing point
  [180, 580], [300, 460], [460, 300], [580, 180],
  // Forward posts covering the approach
  [90, 30], [240, 130],
];
export const TOWER_CABIN_ELEVATION = 13;

function addWatchtowers(parent: THREE.Group): void {
  for (const [x, z] of WATCHTOWERS) {
    const y0 = map05HeightAt(x, z);
    const tower = new THREE.Group();
    const legs = new THREE.Mesh(new THREE.BoxGeometry(3.4, 12, 3.4), TOWER_CONCRETE);
    legs.position.y = 6;
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(7, 3.6, 7), TOWER_CABIN);
    cabin.position.y = 12 + 1.8;
    const roof = new THREE.Mesh(new THREE.BoxGeometry(8, 0.7, 8), TOWER_CONCRETE);
    roof.position.y = 12 + 3.6 + 0.35;
    tower.add(legs, cabin, roof);
    tower.position.set(x, y0, z);
    tower.children.forEach((c) => {
      c.castShadow = true;
      c.receiveShadow = true;
    });
    parent.add(tower);
  }
}

// The double fence: two instanced post rows bracketing the plowed strip.
function addBorderFences(parent: THREE.Group): void {
  const mat = new THREE.MeshStandardMaterial({ color: 0x565650, flatShading: true, roughness: 0.9 });
  const post = new THREE.BoxGeometry(0.7, 5.5, 0.7);
  for (const offset of [-34, 34]) {
    const mesh = new THREE.InstancedMesh(post, mat, 96);
    const matrix = new THREE.Matrix4();
    let placed = 0;
    for (let t = -640; t <= 640 && placed < 96; t += 14) {
      const x = 380 + t / Math.SQRT2 + offset / Math.SQRT2;
      const z = 380 - t / Math.SQRT2 + offset / Math.SQRT2;
      if (Math.max(Math.abs(x), Math.abs(z)) > 900) continue;
      matrix.setPosition(x, map05HeightAt(x, z) + 2.4, z);
      mesh.setMatrixAt(placed, matrix);
      placed++;
    }
    mesh.count = placed;
    mesh.name = `map05-fence-${offset > 0 ? "east" : "west"}`;
    mesh.castShadow = true;
    mesh.frustumCulled = false;
    mesh.userData["losTransparent"] = true;
    mesh.userData["ignoreBallistics"] = true;
    parent.add(mesh);
  }
}

// Farmsteads: barn + house clusters on the fields near the route's early legs.
const BARN_WALL = new THREE.MeshStandardMaterial({ color: 0x7a5844, flatShading: true, roughness: 0.95 });
const BARN_ROOF = new THREE.MeshStandardMaterial({ color: 0x4f4a44, flatShading: true, roughness: 1.0 });

function addFarmstead(parent: THREE.Group, cx: number, cz: number, rnd: () => number): void {
  const buildings = 2 + Math.floor(rnd() * 2);
  for (let i = 0; i < buildings; i++) {
    const x = cx + (rnd() - 0.5) * 60;
    const z = cz + (rnd() - 0.5) * 60;
    if (map05RouteDistanceAt(x, z) < 16) continue;
    const w = 10 + rnd() * 8;
    const d = 7 + rnd() * 5;
    const h = 5 + rnd() * 2;
    const y0 = map05HeightAt(x, z);
    const barn = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), BARN_WALL);
    body.position.y = h / 2;
    const roof = new THREE.Mesh(new THREE.CylinderGeometry(0.1, d * 0.72, w, 3), BARN_ROOF);
    roof.rotation.z = Math.PI / 2;
    roof.rotation.y = Math.PI / 2;
    roof.position.y = h + 1.4;
    barn.add(body, roof);
    barn.position.set(x, y0, z);
    barn.rotation.y = Math.floor(rnd() * 4) * (Math.PI / 2) + FIELD_ANGLE;
    barn.children.forEach((c) => {
      c.castShadow = true;
      c.receiveShadow = true;
    });
    parent.add(barn);
  }
}

function addFarmsteads(parent: THREE.Group): void {
  const rnd = scatterRng(0x5f41c7);
  const sites: Array<[number, number]> = [
    [-390, -340], // ASSEMBLY FARM itself
    [-180, -320],
    [-320, -60],
    [-40, -260],
    [120, -120],
    [-140, 220],
  ];
  for (const [x, z] of sites) addFarmstead(parent, x, z, rnd);
}

export class Map05Terrain {
  readonly group = new THREE.Group();

  constructor(size = 2200) {
    setTerrainHeightProvider(map05HeightAt);
    const geo = new THREE.PlaneGeometry(size, size, 260, 260);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes["position"] as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, map05HeightAt(pos.getX(i), pos.getZ(i)));
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
    paintMap05Ground(geo);
    const ground = new THREE.Mesh(geo, GROUND_MATERIAL);
    ground.name = "map05-ground";
    ground.receiveShadow = true;
    this.group.add(ground);

    this.group.add(makeTrackRibbon(resamplePolyline(ROUTE.map((v) => v.clone()), 12), 11));
    addTreelines(this.group);
    addWatchtowers(this.group);
    addBorderFences(this.group);
    addFarmsteads(this.group);
    this.addFarField(size);
    // Cold overcast horizon — muted olive ridge under grey haze
    addHorizonRing(this.group, size, { ridge: 0x555a48, haze: 0xa9aca4 }, 0x1c0ffee);
  }

  private addFarField(size: number): void {
    const geo = new THREE.CircleGeometry(size * 1.7, 96);
    geo.rotateX(-Math.PI / 2);
    paintFarFieldVertexColors(geo, size * 0.5, { inner: 0x77754e, outer: 0x83805a, dry: 0x6c6947 });
    const disc = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1, metalness: 0 }),
    );
    disc.name = "map05-farfield";
    disc.position.y = -2;
    disc.receiveShadow = true;
    disc.userData["losTransparent"] = true;
    this.group.add(disc);
  }
}
